/*
  Generic scroll-entrance utility, reused across category cards, product
  cards, and static homepage sections (see scroll-reveal.css). Add
  data-scroll-reveal to any element to make it fade/slide in once, the
  first time it enters the viewport.

  - Fires once per element (unobserve after reveal); never replays on
    scroll up/down.
  - Uses only opacity/transform (no layout-triggering properties).
  - Skips entirely if prefers-reduced-motion is set, or if
    IntersectionObserver isn't supported — content simply stays visible
    in its default (non-hidden) state either way.
  - Siblings sharing the same parent get a small automatic stagger via a
    --reveal-delay custom property, capped so large grids don't produce a
    long tail of delay.
  - A MutationObserver watches for [data-scroll-reveal] elements added
    after the initial pass (e.g. collection AJAX filtering/pagination
    replacing .tt-product-listing) and arms + observes those too. Without
    this, newly-inserted cards would carry the attribute (so the CSS
    still hides them at opacity:0) but never get the IntersectionObserver
    that's the only thing that ever adds .is-revealed back — i.e. they'd
    stay invisible forever even though the DOM/product data is correct.
*/
(function () {
  "use strict";

  var targets = document.querySelectorAll("[data-scroll-reveal]");
  if (!targets.length) return;

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if (!("IntersectionObserver" in window)) return;

  document.documentElement.classList.add("tt-scroll-reveal-ready");

  var STEP_MS = 70;
  var MAX_STEPS = 5;
  var groupCounts = new Map();

  // Reveal targets are sometimes nested at different depths depending on
  // which template includes them (e.g. product cards render inside a
  // per-item wrapper div supplied by the calling grid/carousel). Grouping
  // by the nearest common row/grid ancestor — rather than the immediate
  // parent — keeps the stagger consistent across those callers.
  function groupAncestor(el) {
    return el.closest(".row, .tt-product-listing") || el.parentElement;
  }

  var io = new IntersectionObserver(
    function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-revealed");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15, rootMargin: "0px 0px -5% 0px" }
  );

  function arm(el) {
    if (el.hasAttribute("data-scroll-armed")) return;
    el.setAttribute("data-scroll-armed", "");
    var group = groupAncestor(el);
    var index = groupCounts.get(group) || 0;
    groupCounts.set(group, index + 1);
    var delay = Math.min(index, MAX_STEPS) * STEP_MS;
    el.style.setProperty("--reveal-delay", delay + "ms");
    io.observe(el);
  }

  targets.forEach(arm);

  if ("MutationObserver" in window) {
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.hasAttribute("data-scroll-reveal")) arm(node);
          node.querySelectorAll && node.querySelectorAll("[data-scroll-reveal]").forEach(arm);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
